import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Package, Phone, Mail, Star } from 'lucide-react';
import { toast } from 'sonner';
import EditEntityDialog from '../components/EditEntityDialog';
import RowActions from '../components/RowActions';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function Suppliers() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', service_type: 'services', rating: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/suppliers`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Failed to load suppliers'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/suppliers`, newItem, { withCredentials: true });
      toast.success('Supplier added');
      setShowNew(false);
      setNewItem({ name: '', contact_person: '', email: '', phone: '', address: '', service_type: 'services', rating: 0 });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, { withCredentials: true });
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Layout>
      <div data-testid="suppliers-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('supp.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('supp.subtitle')}</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-supplier-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Supplier</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input data-testid="supplier-name-input" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Contact Person</Label><Input value={newItem.contact_person} onChange={(e) => setNewItem({ ...newItem, contact_person: e.target.value })} /></div>
                    <div><Label>Service Type</Label>
                      <Select value={newItem.service_type} onValueChange={(v) => setNewItem({ ...newItem, service_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="services">Services</SelectItem>
                          <SelectItem value="materials">Materials</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input type="email" value={newItem.email} onChange={(e) => setNewItem({ ...newItem, email: e.target.value })} /></div>
                    <div><Label>Phone</Label><Input value={newItem.phone} onChange={(e) => setNewItem({ ...newItem, phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Address</Label><Textarea rows={2} value={newItem.address} onChange={(e) => setNewItem({ ...newItem, address: e.target.value })} /></div>
                  <Button data-testid="submit-supplier-btn" onClick={handleCreate} disabled={!newItem.name} className="w-full bg-teal-600 hover:bg-teal-700">Add Supplier</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => (
              <Card key={s.id} data-testid={`supplier-card-${s.id}`} className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-teal-100 rounded-lg"><Package className="w-5 h-5 text-teal-700" /></div>
                  {MANAGE.includes(user?.role) && (
                    <RowActions
                      testIdPrefix={`supplier-${s.id}`}
                      onEdit={() => setEditingId(s.id)}
                      onDelete={() => handleDelete(s.id)}
                      deleteLabel={`supplier "${s.name}"`}
                    />
                  )}
                </div>
                <h3 className="font-semibold text-foreground mb-1">{s.name}</h3>
                <p className="text-xs text-muted-foreground mb-3 capitalize">{s.service_type}</p>
                <div className="space-y-1 text-sm text-muted-foreground">
                  {s.contact_person && <div>👤 {s.contact_person}</div>}
                  {s.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{s.phone}</div>}
                  {s.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{s.email}</div>}
                </div>
                {s.rating > 0 && (
                  <div className="flex items-center gap-1 mt-3 text-amber-500">
                    <Star className="w-3.5 h-3.5 fill-current" />
                    <span className="text-xs font-semibold">{s.rating.toFixed(1)}</span>
                  </div>
                )}
              </Card>
            ))}
            {items.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No suppliers yet</p>
              </Card>
            )}
          </div>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={items.find(x => x.id === editingId)}
            endpoint="suppliers"
            title="Edit Supplier"
            onSaved={() => { setEditingId(null); fetchData(); }}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'contact_person', label: 'Contact Person' },
              { key: 'service_type', label: 'Service Type', type: 'select', options: [
                { value: 'services', label: 'Services' },
                { value: 'materials', label: 'Materials' },
                { value: 'both', label: 'Both' },
              ]},
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'phone', label: 'Phone', type: 'tel' },
              { key: 'address', label: 'Address', type: 'textarea', rows: 2 },
              { key: 'rating', label: 'Rating (0–5)', type: 'number' },
            ]}
          />
        )}
      </div>
    </Layout>
  );
}
